// Performance Monitor - Tracks FPS and performance metrics
export class PerformanceMonitor {
    constructor(options = {}) {
        this.options = {
            enabled: options.enabled !== false,
            showOverlay: options.showOverlay !== false,
            updateInterval: options.updateInterval || 1000, // ms
            historySize: options.historySize || 60,
            ...options
        };
        
        // Performance metrics
        this.frameCount = 0;
        this.lastUpdateTime = 0;
        this.currentFPS = 0;
        this.averageFPS = 0;
        this.minFPS = Infinity;
        this.maxFPS = 0;
        
        // Frame time tracking
        this.frameTimes = [];
        this.lastFrameTime = 0;
        
        // Memory tracking (if available)
        this.memoryUsage = 0;
        this.memoryPeak = 0;
        
        // Performance warnings
        this.performanceWarnings = [];
        this.warningThresholds = {
            lowFPS: 30,
            highFrameTime: 50, // ms
            highMemoryUsage: 100 * 1024 * 1024 // 100MB
        };
        
        this.init();
    }

    init() {
        if (this.options.enabled) {
            this.createOverlay();
            this.startMemoryTracking();
        }
    }

    createOverlay() {
        if (!this.options.showOverlay) return;
        
        // Create overlay element
        this.overlay = document.createElement('div');
        this.overlay.id = 'performance-monitor';
        this.overlay.style.cssText = `
            position: fixed;
            top: 10px;
            right: 10px;
            background: rgba(0, 0, 0, 0.8);
            color: #00ff00;
            font-family: monospace;
            font-size: 12px;
            padding: 10px;
            border-radius: 5px;
            z-index: 10000;
            min-width: 200px;
            user-select: none;
            pointer-events: none;
        `;
        
        document.body.appendChild(this.overlay);
        
        // Create toggle button
        this.toggleButton = document.createElement('button');
        this.toggleButton.textContent = 'FPS';
        this.toggleButton.style.cssText = `
            position: fixed;
            top: 10px;
            right: 10px;
            background: rgba(0, 0, 0, 0.8);
            color: #00ff00;
            border: none;
            padding: 5px 10px;
            border-radius: 3px;
            cursor: pointer;
            z-index: 10001;
            font-size: 12px;
        `;
        
        this.toggleButton.addEventListener('click', () => {
            this.overlay.style.display = this.overlay.style.display === 'none' ? 'block' : 'none';
        });
        
        document.body.appendChild(this.toggleButton);
    }

    startMemoryTracking() {
        if (performance.memory) {
            this.memoryTrackingInterval = setInterval(() => {
                this.updateMemoryUsage();
            }, 1000);
        }
    }

    updateMemoryUsage() {
        if (!performance.memory) return;
        
        const memory = performance.memory;
        this.memoryUsage = memory.usedJSHeapSize;
        this.memoryPeak = Math.max(this.memoryPeak, this.memoryUsage);
        
        // Check for memory warnings
        if (this.memoryUsage > this.warningThresholds.highMemoryUsage) {
            this.addPerformanceWarning('High memory usage', 'memory');
        }
    }

    update(deltaTime) {
        if (!this.options.enabled) return;
        
        const currentTime = performance.now();
        
        // Update frame time tracking
        if (this.lastFrameTime > 0) {
            const frameTime = currentTime - this.lastFrameTime;
            this.frameTimes.push(frameTime);
            
            if (this.frameTimes.length > this.options.historySize) {
                this.frameTimes.shift();
            }
            
            // Check for frame time warnings
            if (frameTime > this.warningThresholds.highFrameTime) {
                this.addPerformanceWarning('High frame time', 'frameTime');
            }
        }
        
        this.lastFrameTime = currentTime;
        this.frameCount++;
        
        // Update FPS every interval
        if (currentTime - this.lastUpdateTime >= this.options.updateInterval) {
            this.calculateFPS(currentTime);
            this.updateOverlay();
            this.lastUpdateTime = currentTime;
        }
    }

    calculateFPS(currentTime) {
        const elapsed = currentTime - this.lastUpdateTime;
        const fps = (this.frameCount * 1000) / elapsed;
        
        this.currentFPS = Math.round(fps);
        this.frameCount = 0;
        
        // Update statistics
        this.minFPS = Math.min(this.minFPS, this.currentFPS);
        this.maxFPS = Math.max(this.maxFPS, this.currentFPS);
        
        // Calculate average FPS
        if (this.frameTimes.length > 0) {
            const avgFrameTime = this.frameTimes.reduce((a, b) => a + b) / this.frameTimes.length;
            this.averageFPS = Math.round(1000 / avgFrameTime);
        }
        
        // Check for FPS warnings
        if (this.currentFPS < this.warningThresholds.lowFPS) {
            this.addPerformanceWarning('Low FPS', 'fps');
        }
    }

    addPerformanceWarning(message, type) {
        const warning = {
            message: message,
            type: type,
            timestamp: performance.now(),
            fps: this.currentFPS
        };
        
        this.performanceWarnings.push(warning);
        
        // Keep only recent warnings
        if (this.performanceWarnings.length > 10) {
            this.performanceWarnings.shift();
        }
        
        console.warn(`Performance Warning: ${message} (FPS: ${this.currentFPS})`);
    }

    updateOverlay() {
        if (!this.overlay) return;
        
        const avgFrameTime = this.frameTimes.length > 0 
            ? (this.frameTimes.reduce((a, b) => a + b) / this.frameTimes.length).toFixed(2)
            : '0.00';
        
        let memoryInfo = '';
        if (performance.memory) {
            memoryInfo = `
Memory: ${this.formatBytes(this.memoryUsage)}
Peak: ${this.formatBytes(this.memoryPeak)}`;
        }
        
        let warningsInfo = '';
        if (this.performanceWarnings.length > 0) {
            const recentWarnings = this.performanceWarnings.slice(-3);
            warningsInfo = `
Warnings: ${recentWarnings.map(w => w.message).join(', ')}`;
        }
        
        this.overlay.innerHTML = `
FPS: ${this.currentFPS} (${this.minFPS}-${this.maxFPS})
Avg: ${this.averageFPS}
Frame: ${avgFrameTime}ms${memoryInfo}${warningsInfo}
        `.trim();
        
        // Update color based on performance
        if (this.currentFPS < 30) {
            this.overlay.style.color = '#ff0000'; // Red for poor performance
        } else if (this.currentFPS < 50) {
            this.overlay.style.color = '#ffff00'; // Yellow for moderate performance
        } else {
            this.overlay.style.color = '#00ff00'; // Green for good performance
        }
    }

    formatBytes(bytes) {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    render(renderer) {
        if (!this.options.enabled || !this.options.showOverlay) return;
        
        // Render performance overlay on canvas
        const ctx = renderer.getContext();
        if (!ctx) return;
        
        ctx.save();
        
        // Background
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(10, 10, 200, 80);
        
        // Text
        ctx.fillStyle = this.currentFPS < 30 ? '#ff0000' : '#00ff00';
        ctx.font = '12px monospace';
        ctx.textAlign = 'left';
        
        ctx.fillText(`FPS: ${this.currentFPS}`, 20, 30);
        ctx.fillText(`Avg: ${this.averageFPS}`, 20, 45);
        ctx.fillText(`Frame: ${this.frameTimes.length > 0 ? (this.frameTimes[this.frameTimes.length - 1]).toFixed(2) : '0.00'}ms`, 20, 60);
        
        if (performance.memory) {
            ctx.fillText(`Mem: ${this.formatBytes(this.memoryUsage)}`, 20, 75);
        }
        
        ctx.restore();
    }

    // Getters
    getFPS() {
        return this.currentFPS;
    }

    getAverageFPS() {
        return this.averageFPS;
    }

    getMinFPS() {
        return this.minFPS;
    }

    getMaxFPS() {
        return this.maxFPS;
    }

    getFrameTimes() {
        return [...this.frameTimes];
    }

    getMemoryUsage() {
        return this.memoryUsage;
    }

    getMemoryPeak() {
        return this.memoryPeak;
    }

    getPerformanceWarnings() {
        return [...this.performanceWarnings];
    }

    isEnabled() {
        return this.options.enabled;
    }

    // Setters
    setEnabled(enabled) {
        this.options.enabled = enabled;
        if (enabled) {
            this.createOverlay();
        } else if (this.overlay) {
            this.overlay.remove();
            this.toggleButton.remove();
        }
    }

    setShowOverlay(show) {
        this.options.showOverlay = show;
        if (show && this.options.enabled) {
            this.createOverlay();
        } else if (this.overlay) {
            this.overlay.remove();
            this.toggleButton.remove();
        }
    }

    reset() {
        this.frameCount = 0;
        this.lastUpdateTime = 0;
        this.currentFPS = 0;
        this.averageFPS = 0;
        this.minFPS = Infinity;
        this.maxFPS = 0;
        this.frameTimes = [];
        this.memoryUsage = 0;
        this.memoryPeak = 0;
        this.performanceWarnings = [];
    }
}